import { timestampDate } from '@bufbuild/protobuf/wkt';
import { differenceInSeconds } from '@qdrant-cloud-ui/date/difference-in-seconds';
import { type Toleration, TolerationEffect, TolerationOperator } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import {
  type HybridCloudEnvironment,
  HybridCloudEnvironmentComponentStatusPhase,
  HybridCloudEnvironmentConfigurationLogLevel,
  type HybridCloudEnvironmentStatus,
  HybridCloudEnvironmentStatusPhase,
  KubernetesDistribution,
  QdrantClusterCreationStatus,
} from '@qdrant/qdrant-cloud-public-api/hybrid/v1';
import { snakeCaseToTitleCase } from '@/utils/string-formatters';

export type PhaseString = 'Ready' | 'NotReady' | 'Unknown';
const DEFAULT_PHASE = 'Unknown';

/**
 * Defines how often we should poll for data.
 */
export const DEFAULT_REFRESH_INTERVAL = 30_000;

const TWO_MINUTES_IN_SECS = 60 * 2;

export type HybridCloudEnvStage = 'setup' | 'connection' | 'ready' | null;
export type HybridCloudEnvComponentStatuses = {
  kubernetesVersion: string;
  operatorV2Version: string;
  clusterManagerVersion: string;
  agentVersion: string;
  prometheusVersion: string;
  qdrantNodeExporterVersion: string;
  operatorV2PodState: PhaseString;
  clusterManagerPodState: PhaseString;
  agentPodState: PhaseString;
  prometheusPodState: PhaseString;
  qdrantNodeExporterPodState: PhaseString;
  lastModifiedAt?: Date;
  phase: PhaseString;
  stage: HybridCloudEnvStage;
};

export const getComponentPhaseString = (phase?: HybridCloudEnvironmentComponentStatusPhase): PhaseString => {
  switch (phase) {
    case HybridCloudEnvironmentComponentStatusPhase.READY:
      return 'Ready';
    case HybridCloudEnvironmentComponentStatusPhase.NOT_READY:
    case HybridCloudEnvironmentComponentStatusPhase.NOT_FOUND:
      return 'NotReady';
    case HybridCloudEnvironmentComponentStatusPhase.UNSPECIFIED:
    default:
      return DEFAULT_PHASE;
  }
};

export const getEnvPhaseString = (phase?: HybridCloudEnvironmentStatusPhase): PhaseString => {
  switch (phase) {
    case HybridCloudEnvironmentStatusPhase.READY:
      return 'Ready';
    case HybridCloudEnvironmentStatusPhase.NOT_READY:
    case HybridCloudEnvironmentStatusPhase.FAILED_TO_SYNC:
      return 'NotReady';
    case HybridCloudEnvironmentStatusPhase.UNSPECIFIED:
    default:
      return DEFAULT_PHASE;
  }
};

export const mapLogLevelToString = (logLevel?: HybridCloudEnvironmentConfigurationLogLevel): string => {
  switch (logLevel) {
    case HybridCloudEnvironmentConfigurationLogLevel.DEBUG:
    case HybridCloudEnvironmentConfigurationLogLevel.WARN:
    case HybridCloudEnvironmentConfigurationLogLevel.ERROR:
      return HybridCloudEnvironmentConfigurationLogLevel[logLevel].split(' ').join('');
    case HybridCloudEnvironmentConfigurationLogLevel.UNSPECIFIED:
    default:
      return 'INFO';
  }
};

export const KUBERNETES_DISTRIBUTION_MAP = {
  [KubernetesDistribution.AWS]: 'aws',
  [KubernetesDistribution.AZURE]: 'azure',
  [KubernetesDistribution.CIVO]: 'civo',
  [KubernetesDistribution.DO]: 'do',
  [KubernetesDistribution.GCP]: 'gcp',
  [KubernetesDistribution.K3S]: 'k3s',
  [KubernetesDistribution.LINODE]: 'linode',
  [KubernetesDistribution.OCI]: 'oci',
  [KubernetesDistribution.OPENSHIFT]: 'openshift',
  [KubernetesDistribution.OVHCLOUD]: 'ovhcloud',
  [KubernetesDistribution.SCALEWAY]: 'scaleway',
  [KubernetesDistribution.STACKIT]: 'stackit',
  [KubernetesDistribution.VULTR]: 'vultr',
  [KubernetesDistribution.UNSPECIFIED]: 'unknown',
};

/**
 * Returns the color of the status pill based on the status value.
 */
export const getStatusColor = (status?: PhaseString) => {
  switch (status) {
    case 'Ready':
      return 'success';
    case 'NotReady':
      return 'error';
    case 'Unknown':
      return 'warning';
    default:
      return 'secondary';
  }
};

export const getStatusLabel = (state: string | undefined, version: string | undefined): string => {
  let label = state ?? DEFAULT_PHASE;
  if (version) {
    label = `${label} (${version})`;
  }
  return label;
};

/**
 * Checks if the last status update is older than 2 minutes. (No status => false).
 */
export const isOutdatedStatusData = (status?: HybridCloudEnvironmentStatus): boolean => {
  if (!status) {
    return false;
  }

  return (
    status.lastModifiedAt != null &&
    differenceInSeconds(new Date(), timestampDate(status.lastModifiedAt)) > TWO_MINUTES_IN_SECS
  );
};

export const isHybridCloudEnvAvailable = (hybridCloudEnv: HybridCloudEnvironment) =>
  hybridCloudEnv.status?.clusterCreationReadiness === QdrantClusterCreationStatus.READY;

const getCuratedPhase = (
  phase?: HybridCloudEnvironmentComponentStatusPhase,
): HybridCloudEnvironmentComponentStatusPhase => phase ?? HybridCloudEnvironmentComponentStatusPhase.UNSPECIFIED;

export const getHybridCloudEnvComponentStatuses = (
  hybridCloudEnv?: HybridCloudEnvironment,
): HybridCloudEnvComponentStatuses => {
  const status = hybridCloudEnv?.status;
  const operatorV2Data = status?.componentStatuses.find(({ name }) => name === 'qdrant-operator-v2');
  const clusterManagerData = status?.componentStatuses.find(({ name }) => name === 'qdrant-cluster-manager');
  const agentData = status?.componentStatuses.find(({ name }) => name === 'qdrant-cloud-agent');
  const prometheusData = status?.componentStatuses.find(({ name }) => name === 'qdrant-prometheus');
  const qdrantNodeExporterData = status?.componentStatuses.find(({ name }) => name === 'qdrant-node-exporter');

  return {
    agentPodState: getComponentPhaseString(getCuratedPhase(agentData?.phase)),
    agentVersion: agentData?.version ?? '-',
    clusterManagerPodState: getComponentPhaseString(getCuratedPhase(clusterManagerData?.phase)),
    clusterManagerVersion: clusterManagerData?.version ?? '-',
    kubernetesVersion: status?.kubernetesVersion ?? '-',
    lastModifiedAt: status?.lastModifiedAt ? timestampDate(status.lastModifiedAt) : undefined,
    operatorV2PodState: getComponentPhaseString(getCuratedPhase(operatorV2Data?.phase)),
    operatorV2Version: operatorV2Data?.version ?? '-',
    phase: getEnvPhaseString(status?.phase),
    prometheusPodState: getComponentPhaseString(getCuratedPhase(prometheusData?.phase)),
    prometheusVersion: prometheusData?.version ?? '-',
    qdrantNodeExporterPodState: getComponentPhaseString(getCuratedPhase(qdrantNodeExporterData?.phase)),
    qdrantNodeExporterVersion: qdrantNodeExporterData?.version ?? '-',
    stage: getHybridCloudEnvironmentStage(hybridCloudEnv),
  };
};

export const getGeneralStatusPillDetails = (
  hybridCloudEnvironment?: HybridCloudEnvironment,
): { color: ReturnType<typeof getStatusColor>; label: string } | null => {
  if (!hybridCloudEnvironment?.status) {
    return null;
  }
  const statusData = getHybridCloudEnvComponentStatuses(hybridCloudEnvironment);
  const statusColor = getStatusColor(statusData.phase);
  return {
    color: statusColor,
    label: statusData.phase,
  };
};

export type HybridCloudEnvParsedStatus = ReturnType<typeof getHybridCloudEnvParsedStatus>;
export function getHybridCloudEnvParsedStatus(hybridCloudEnv?: HybridCloudEnvironment) {
  if (!hybridCloudEnv?.status) {
    return null;
  }

  const statusData = getHybridCloudEnvComponentStatuses(hybridCloudEnv);
  return {
    ...statusData,
    modifiedAt: statusData.lastModifiedAt,
    numberOfNodes: hybridCloudEnv.status.numberOfNodes,
    capabilities: hybridCloudEnv.status.capabilities,
  };
}

export const getTolerationEffectString = (effect?: TolerationEffect): string => {
  if (!effect) {
    return 'All';
  }

  return snakeCaseToTitleCase(TolerationEffect[effect]).split(' ').join('');
};

export const getTolerationOperatorString = (operator?: TolerationOperator): string => {
  if (!operator) {
    return '';
  }

  return snakeCaseToTitleCase(TolerationOperator[operator]).split(' ').join('');
};

export const mapTolerationWithEnumsToString = (toleration: Toleration) => ({
  ...toleration,
  effect: getTolerationEffectString(toleration.effect),
  operator: getTolerationOperatorString(toleration.operator),
});

export const getHybridCloudEnvironmentStage = (
  hybridCloudEnvironment?: HybridCloudEnvironment,
): HybridCloudEnvStage => {
  if (!hybridCloudEnvironment) {
    return null;
  } else if (!hybridCloudEnvironment.status) {
    return 'connection';
  } else {
    return 'ready';
  }
};
